import React, { useEffect, useState } from "react";
import { CreditCard, ShieldAlert, ShieldCheck, KeyRound } from "lucide-react";
import api from "@/lib/api";

/**
 * Read-only Stripe payments configuration dashboard.
 * Never displays raw secrets — only masked values + booleans + mode.
 */
export default function AdminPaymentsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/admin/payments/settings")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load"));
  }, []);

  const toneClasses = {
    danger: "bg-red-50 border-red-200 text-red-800",
    warning: "bg-amber-50 border-amber-200 text-amber-900",
    neutral: "bg-slate-50 border-slate-200 text-slate-700",
  };

  return (
    <div className="max-w-4xl" data-testid="admin-payments">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Payments</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">
          Stripe configuration
        </h1>
        <p className="text-[#252A34]/70 text-sm mt-1">
          Read-only status of the platform's payment credentials. All values are masked; raw
          secrets never leave the server.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="admin-payments-error">
          {error}
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* Mode banner */}
          <div
            className={`rounded-2xl border p-5 ${toneClasses[data.status_tone] || toneClasses.neutral}`}
            data-testid="admin-payments-mode-banner"
          >
            <div className="flex items-start gap-3">
              {data.mode === "live" ? (
                <ShieldAlert className="h-5 w-5 mt-0.5" />
              ) : (
                <ShieldCheck className="h-5 w-5 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="font-display text-lg font-semibold" data-testid="admin-payments-status-label">
                  {data.status_label}
                </div>
                <div className="text-sm mt-1 leading-relaxed">{data.note}</div>
              </div>
              <div className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider" data-testid="admin-payments-mode-chip">
                Mode: {data.mode}
              </div>
            </div>
          </div>

          {/* Active key */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-display text-base font-semibold text-[#071b22] mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-[#165a71]" /> Active key (what Stripe requests use right now)
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-[#252A34]/60">Kind</dt>
                <dd className="mt-1 font-mono text-[#071b22]" data-testid="admin-payments-active-kind">
                  {data.active_key_kind}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-[#252A34]/60">Value (masked)</dt>
                <dd className="mt-1 font-mono text-[#071b22]" data-testid="admin-payments-active-masked">
                  {data.active_key_masked || "—"}
                </dd>
              </div>
            </dl>
          </section>

          {/* Test / Live configuration matrix */}
          <section className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-payments-test-card">
              <h3 className="font-display text-base font-semibold text-[#071b22] mb-3 flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-emerald-600" /> Test environment
              </h3>
              <ul className="space-y-2 text-sm">
                <ConfigRow label="Test secret key" ok={data.test_key_configured} testid="cfg-test-key" />
                <ConfigRow label="Test webhook secret" ok={data.test_webhook_configured} testid="cfg-test-webhook" />
              </ul>
              <p className="text-xs text-[#252A34]/60 mt-3">
                Every checkout in this environment runs against Stripe test mode. Use card{" "}
                <span className="font-mono">4242 4242 4242 4242</span> with any future expiry.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-payments-live-card">
              <h3 className="font-display text-base font-semibold text-[#071b22] mb-3 flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-red-600" /> Live environment
              </h3>
              <ul className="space-y-2 text-sm">
                <ConfigRow label="Live secret key (sk_live_...)" ok={data.live_secret_configured} testid="cfg-live-secret" />
                <ConfigRow
                  label={
                    <span>
                      Live restricted key (rk_live_...)
                      {data.live_restricted_masked && (
                        <span className="ml-2 font-mono text-xs text-[#252A34]/60">
                          {data.live_restricted_masked}
                        </span>
                      )}
                    </span>
                  }
                  ok={data.live_restricted_configured}
                  testid="cfg-live-restricted"
                />
                <ConfigRow label="Live webhook secret" ok={data.live_webhook_configured} testid="cfg-live-webhook" />
                <ConfigRow label="Ready to activate live mode" ok={data.can_go_live} testid="cfg-live-ready" />
              </ul>
              {data.live_disabled_reason && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3" data-testid="admin-payments-live-reason">
                  {data.live_disabled_reason}
                </p>
              )}
            </div>
          </section>

          {/* Go-live checklist */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-display text-base font-semibold text-[#071b22] mb-2">
              Production go-live checklist
            </h3>
            <ol className="list-decimal ml-5 text-sm text-[#252A34] space-y-1.5">
              <li>Add <span className="font-mono">STRIPE_LIVE_SECRET_KEY</span> (sk_live_...) and <span className="font-mono">STRIPE_LIVE_PUBLISHABLE_KEY</span> to backend .env.</li>
              <li>Register a webhook endpoint on the production domain <span className="font-mono">/api/stripe/webhook</span> and paste its signing secret into <span className="font-mono">STRIPE_LIVE_WEBHOOK_SECRET</span>.</li>
              <li>Set <span className="font-mono">STRIPE_MODE=live</span> and restart the backend.</li>
              <li>Verify <span className="font-mono">GET /api/admin/payments/settings</span> reports <em>Ready to activate live mode: yes</em>.</li>
              <li>Run a small ($1) test transaction end-to-end before opening flows to customers.</li>
            </ol>
          </section>
        </div>
      )}
    </div>
  );
}

function ConfigRow({ label, ok, testid }) {
  return (
    <li className="flex items-center justify-between gap-3" data-testid={testid}>
      <span className="text-[#252A34]">{label}</span>
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
          ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
        }`}
      >
        {ok ? "Configured" : "Missing"}
      </span>
    </li>
  );
}
